import React, { useId, useRef, useState } from 'react';
import { Button, Checkbox, Input, Modal } from 'antd';
import type { InputRef } from 'antd/es/input';
import { ResumeEducationEntry, ResumeExperienceEntry } from '../../types';
import css from './EntryEditorModal.module.scss';

/** What the modal is editing. A null `entry` means "add a new one". */
export type EntryEditorTarget =
  | { kind: 'experience'; entry: ResumeExperienceEntry | null }
  | { kind: 'education'; entry: ResumeEducationEntry | null };

/** What comes back out: the same discriminant the caller sent in, so the
 *  payload can only ever be filed under the section it came from. */
export type EntryEditorDraft =
  | { kind: 'experience'; entry: ResumeExperienceEntry }
  | { kind: 'education'; entry: ResumeEducationEntry };

interface EntryEditorModalProps {
  open: boolean;
  target: EntryEditorTarget;
  /** True while the save round-trips — freezes the modal, as in CommentPopover. */
  busy: boolean;
  /** The saved entry, tagged with its kind. Tagged rather than a bare union so
   *  the caller narrows instead of casting — an untagged payload makes
   *  `draft as ResumeExperienceEntry` on the education branch compile silently
   *  and write a school into `content.experience`. */
  onSubmit: (draft: EntryEditorDraft) => void;
  onClose: () => void;
}

// The stored shape for experience dates: "YYYY" or "YYYY-MM", month 01..12.
// Deliberately strict — "2020-6" and "2020-13" both render verbatim into the
// PDF, so a lenient regex here becomes a typo the user sees in front of a
// recruiter.
const MONTH_OR_YEAR = /^\d{4}(-(0[1-9]|1[0-2]))?$/;
// Education stores a year only, because a year only is all any renderer prints.
const YEAR_ONLY = /^\d{4}$/;

const DATE_FORMAT_ERROR = 'Use YYYY or YYYY-MM — for example 2021 or 2021-06.';
const YEAR_FORMAT_ERROR = 'Use a 4-digit year — for example 2021.';
// The HTML templates, the DOCX renderer and the plain-text projection all hide
// the whole date range when `start` is empty (`{% if e.start %}`), so an
// end-only date would be an edit the user could never see anywhere.
const START_REQUIRED_ERROR =
  'Add a start date — the resume shows dates as a range.';

// "2019" and "2019-03" are not comparable as strings until the bare year is
// widened to a month — otherwise "2019" > "2019-03" lexically and a legitimate
// Jan-2019 → Mar-2019 stint reads as time travel.
//
// The two sides widen to OPPOSITE ends of the year, because a bare year means
// "somewhere in 2019" and each side should take the reading that keeps an
// honest entry legal: a start of "2019" is at earliest Jan, an end of "2019" is
// at latest Dec. Parsers routinely produce mixed granularity, so widening both
// to -01 would reject "2019-06 → 2019" — a range with nothing wrong with it,
// escapable only by editing a field the user came to keep.
const comparableDate = (value: string, fallbackMonth: '01' | '12') =>
  value.length === 4 ? `${value}-${fallbackMonth}` : value;

// One flat bag for both kinds. The two forms never render at the same time, so
// a discriminated pair of states would only add branching to every setter.
interface FormState {
  title: string;
  company: string;
  location: string;
  institution: string;
  degree: string;
  start: string;
  end: string;
  current: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  company: '',
  location: '',
  institution: '',
  degree: '',
  start: '',
  end: '',
  current: false,
};

const formFromTarget = (target: EntryEditorTarget): FormState => {
  if (!target.entry) return EMPTY_FORM;
  if (target.kind === 'experience') {
    const entry = target.entry;
    return {
      ...EMPTY_FORM,
      title: entry.title ?? '',
      company: entry.company ?? '',
      location: entry.location ?? '',
      start: entry.start ?? '',
      end: entry.end ?? '',
      // "Still here" is only meaningful against a start date. An entry with
      // NEITHER date is one we never parsed dates for, not an open-ended job —
      // pre-checking it there would trap the user behind the start-date rule
      // while they came to fix a typo in the company name.
      current: Boolean(entry.start) && !entry.end,
    };
  }
  const entry = target.entry;
  return {
    ...EMPTY_FORM,
    institution: entry.institution ?? '',
    degree: entry.degree ?? '',
    end: entry.end ?? '',
  };
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

/** Label + control + optional hint + inline error, wired for screen readers. */
const Field: React.FC<{
  htmlFor: string;
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ htmlFor, label, hint, error, children }) => (
  <div className={css.field}>
    <label className={css.label} htmlFor={htmlFor}>
      {label}
    </label>
    {children}
    {hint && (
      <p className={css.hint} id={`${htmlFor}-hint`}>
        {hint}
      </p>
    )}
    {/* role="alert" so the failure is announced at submit time, not just seen. */}
    {error && (
      <p className={css.error} id={`${htmlFor}-error`} role="alert">
        {error}
      </p>
    )}
  </div>
);

/**
 * Structural editor for one work-history or education entry.
 *
 * Why a form and not the comment box: a date is a fact the user already knows.
 * Routing it through the LLM costs seconds, drops fields in both directions and
 * cannot produce a structured entry at the end — users who tried got fabricated
 * content twice. This form is instant, lossless and deterministic.
 *
 * The backend validates none of these rules, so this is the only guard. It also
 * never touches bullets: they stay the comment box's job and pass through by
 * reference.
 */
const EntryEditorModal: React.FC<EntryEditorModalProps> = ({
  open,
  target,
  busy,
  onSubmit,
  onClose,
}) => {
  const [form, setForm] = useState<FormState>(() => formFromTarget(target));
  const [errors, setErrors] = useState<FieldErrors>({});
  const firstFieldRef = useRef<InputRef>(null);

  // What the form is currently seeded from: the subject's identity as a stable
  // string while open, null while closed. Identity — not the `target` object,
  // which the parent rebuilds inline on every render, so depending on it would
  // wipe the user's keystrokes on every keypress.
  //
  // Two resets fall out of one comparison. Closing nulls the seed, so the next
  // open re-seeds even on the same entry ("each open is a fresh form"). And a
  // target swapped UNDERNEATH an open modal re-seeds too — which is what makes
  // the dangerous case unrepresentable: `submit()` reads `target` from the
  // current render but `form` from the last seed, so without this it would
  // write job A's typed facts onto job B's id and bullets. Silent corruption in
  // a feature whose whole premise is losslessness.
  //
  // Done during render rather than in an effect: this is React's documented
  // "adjusting state when a prop changes" pattern, and it re-seeds before the
  // browser paints, so no stale frame is ever shown.
  const seed = open ? `${target.kind}:${target.entry?.id ?? ''}` : null;
  const [seededFrom, setSeededFrom] = useState<string | null>(seed);
  if (seed !== seededFrom) {
    setSeededFrom(seed);
    if (seed !== null) {
      // Values AND stale errors, so nothing bleeds from the previous subject.
      setForm(formFromTarget(target));
      setErrors({});
    }
  }

  const uid = useId();
  const formId = `${uid}-form`;
  const fieldId = (name: keyof FormState) => `${uid}-${name}`;
  const describedBy = (name: keyof FormState, hasHint = false) => {
    const parts: string[] = [];
    if (hasHint) parts.push(`${fieldId(name)}-hint`);
    if (errors[name]) parts.push(`${fieldId(name)}-error`);
    return parts.length ? parts.join(' ') : undefined;
  };

  const set = (name: keyof FormState, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const isAdd = target.entry === null;
  const isExperience = target.kind === 'experience';

  const submitExperience = (entry: ResumeExperienceEntry | null) => {
    const title = form.title.trim();
    const company = form.company.trim();
    const start = form.start.trim();
    // Checking "I currently work here" IS the end date: null.
    const end = form.current ? '' : form.end.trim();

    const next: FieldErrors = {};
    if (!title) next.title = 'Add a job title.';
    if (!company) next.company = 'Add a company name.';
    if (start && !MONTH_OR_YEAR.test(start)) next.start = DATE_FORMAT_ERROR;
    if (end && !MONTH_OR_YEAR.test(end)) next.end = DATE_FORMAT_ERROR;
    if (!start && (end || form.current)) next.start = START_REQUIRED_ERROR;
    if (
      !next.start &&
      !next.end &&
      start &&
      end &&
      comparableDate(end, '12') < comparableDate(start, '01')
    ) {
      next.end = 'The end date is before the start date.';
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      kind: 'experience',
      entry: {
        // Server-assigned ids are a hard invariant: an add submits '' and the
        // server mints the real one. Never generate one here.
        id: entry?.id ?? '',
        title,
        company,
        start: start || null,
        end: form.current ? null : end || null,
        location: form.location.trim() || null,
        // Bullets are edited through the comment flow only — pass them straight
        // back so a date fix can never drop the user's achievements. The ARRAY
        // is copied, so a downstream splice or reorder cannot reach back into
        // the entry the page still holds. The ResumeBullet objects inside stay
        // shared; shallow is the right depth, because this form never reads a
        // bullet, let alone edits one.
        bullets: [...(entry?.bullets ?? [])],
      },
    });
  };

  const submitEducation = (entry: ResumeEducationEntry | null) => {
    const institution = form.institution.trim();
    const degree = form.degree.trim();
    const end = form.end.trim();

    const next: FieldErrors = {};
    // The backend accepts an all-empty entry; it renders as a blank line on the
    // resume, which no one would ever want on purpose.
    if (!institution && !degree) {
      next.institution = 'Add a school or a degree — one of the two is enough.';
    }
    if (end && !YEAR_ONLY.test(end)) next.end = YEAR_FORMAT_ERROR;

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      kind: 'education',
      entry: {
        id: entry?.id ?? '',
        institution: institution || null,
        degree: degree || null,
        // Nothing renders education `start` — no template, no DOCX, no
        // plain-text projection — so the form shows no control for it. It still
        // round-trips untouched rather than being silently cleared.
        start: entry?.start ?? null,
        end: end || null,
      },
    });
  };

  const submit = (event?: React.FormEvent) => {
    // A real <form> means Enter reaches here natively; stop the browser from
    // then navigating away to submit it for real.
    event?.preventDefault();
    if (busy) return;
    if (target.kind === 'experience') submitExperience(target.entry);
    else submitEducation(target.entry);
  };

  const title = isExperience
    ? isAdd
      ? 'Add a job'
      : 'Edit job details'
    : isAdd
      ? 'Add education'
      : 'Edit education';

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      // While the save round-trips the modal must not silently vanish — Esc,
      // the mask and the close X are all gated to match the disabled Cancel
      // button. (CommentPopover gates the first two but not the X; same hole,
      // left alone as out of scope.)
      keyboard={!busy}
      maskClosable={!busy}
      closable={!busy}
      // antd's focus trap grabs the dialog wrapper on open, so a bare autoFocus
      // is unreliable (especially on reopen) — focus the first field once the
      // open motion settles instead.
      afterOpenChange={(opened) => {
        if (opened) firstFieldRef.current?.focus();
      }}
      width="min(560px, 94vw)"
      className={css.entryModal}
      footer={
        <div className={css.footer}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/* antd renders the footer outside the modal body, so the button is
              tied to the form by id rather than by nesting — that keeps ONE
              submit path for both the click and the Enter key. */}
          <Button
            type="primary"
            htmlType="submit"
            form={formId}
            loading={busy}
            disabled={busy}
          >
            {isAdd ? 'Add' : 'Save'}
          </Button>
        </div>
      }
    >
      {/* A real <form>, not a div: every control here is single-line, so Enter
          has no other meaning, and a keyboard user finishing the last field
          expects it to save. Without the element Enter does nothing at all and
          they must Tab past Cancel to reach the primary action. */}
      <form id={formId} aria-label={title} onSubmit={submit}>
        {isExperience ? (
          <>
            <Field
              htmlFor={fieldId('title')}
              label="Job title"
              error={errors.title}
            >
              <Input
                ref={firstFieldRef}
                id={fieldId('title')}
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Senior Product Manager"
                aria-invalid={Boolean(errors.title)}
                aria-describedby={describedBy('title')}
                disabled={busy}
                dir="auto"
              />
            </Field>

            <Field
              htmlFor={fieldId('company')}
              label="Company"
              error={errors.company}
            >
              <Input
                id={fieldId('company')}
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
                placeholder="Acme"
                aria-invalid={Boolean(errors.company)}
                aria-describedby={describedBy('company')}
                disabled={busy}
                dir="auto"
              />
            </Field>

            <div className={css.dateRow}>
              <Field
                htmlFor={fieldId('start')}
                label="Start date"
                hint="YYYY or YYYY-MM"
                error={errors.start}
              >
                <Input
                  id={fieldId('start')}
                  value={form.start}
                  onChange={(e) => set('start', e.target.value)}
                  placeholder="2021-06"
                  aria-invalid={Boolean(errors.start)}
                  aria-describedby={describedBy('start', true)}
                  disabled={busy}
                  // Dates are always left-to-right, even inside an RTL resume.
                  dir="ltr"
                />
              </Field>

              <Field
                htmlFor={fieldId('end')}
                label="End date"
                hint="YYYY or YYYY-MM"
                error={errors.end}
              >
                <Input
                  id={fieldId('end')}
                  // Showing "2024-08" in a greyed box next to a ticked "I
                  // currently work here" reads as a contradiction. Blank the
                  // display instead — the value stays in state, so unticking
                  // hands the user their date back.
                  value={form.current ? '' : form.end}
                  onChange={(e) => set('end', e.target.value)}
                  placeholder={form.current ? 'Present' : '2024-08'}
                  aria-invalid={Boolean(errors.end)}
                  aria-describedby={describedBy('end', true)}
                  disabled={busy || form.current}
                  dir="ltr"
                />
              </Field>
            </div>

            <Checkbox
              className={css.currentCheckbox}
              checked={form.current}
              onChange={(e) => set('current', e.target.checked)}
              disabled={busy}
            >
              I currently work here
            </Checkbox>

            <Field htmlFor={fieldId('location')} label="Location">
              <Input
                id={fieldId('location')}
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder="Optional — e.g. Tel Aviv"
                disabled={busy}
                dir="auto"
              />
            </Field>
          </>
        ) : (
          <>
            <Field
              htmlFor={fieldId('institution')}
              label="School or organization"
              error={errors.institution}
            >
              <Input
                ref={firstFieldRef}
                id={fieldId('institution')}
                value={form.institution}
                onChange={(e) => set('institution', e.target.value)}
                placeholder="Technion"
                aria-invalid={Boolean(errors.institution)}
                aria-describedby={describedBy('institution')}
                disabled={busy}
                dir="auto"
              />
            </Field>

            {/* No error slot: the "one of the two" rule reports on the field
              above, so a single message covers the pair. */}
            <Field htmlFor={fieldId('degree')} label="Degree or certification">
              <Input
                id={fieldId('degree')}
                value={form.degree}
                onChange={(e) => set('degree', e.target.value)}
                placeholder="BSc Computer Science"
                disabled={busy}
                dir="auto"
              />
            </Field>

            <Field
              htmlFor={fieldId('end')}
              label="End year"
              hint="Your resume shows the end year only."
              error={errors.end}
            >
              <Input
                id={fieldId('end')}
                value={form.end}
                onChange={(e) => set('end', e.target.value)}
                placeholder="2015"
                aria-invalid={Boolean(errors.end)}
                aria-describedby={describedBy('end', true)}
                disabled={busy}
                dir="ltr"
              />
            </Field>
          </>
        )}
      </form>
    </Modal>
  );
};

export default EntryEditorModal;
